import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as offersService from '../../api/offersService';

export const getOffersThunk = createAsyncThunk(
  'offers/getOffers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await offersService.getOffers();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch offers');
    }
  }
);

export const getOfferByIdThunk = createAsyncThunk(
  'offers/getOfferById',
  async (offerId, { rejectWithValue }) => {
    try {
      const data = await offersService.getOfferById(offerId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch offer details');
    }
  }
);

export const acceptOfferThunk = createAsyncThunk(
  'offers/acceptOffer',
  async ({ offerId }, { rejectWithValue }) => {
    try {
      const data = await offersService.acceptOffer(offerId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to accept offer');
    }
  }
);

export const declineOfferThunk = createAsyncThunk(
  'offers/declineOffer',
  async ({ offerId, reason }, { rejectWithValue }) => {
    try {
      const data = await offersService.declineOffer(offerId, reason);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to decline offer');
    }
  }
);

export const updateDOJThunk = createAsyncThunk(
  'offers/updateDOJ',
  async ({ offerId, newDOJ }, { rejectWithValue }) => {
    try {
      const data = await offersService.updateDOJ(offerId, newDOJ);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update date of joining');
    }
  }
);

export const withdrawOfferThunk = createAsyncThunk(
  'offers/withdrawOffer',
  async ({ offerId, reason }, { rejectWithValue }) => {
    try {
      const data = await offersService.withdrawOffer(offerId, reason);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to withdraw offer');
    }
  }
);

const initialState = {
  list: [],
  selectedOffer: null,
  loading: false,
  error: null,
  acceptLoading: false,
  declineLoading: false,
  dojLoading: false,
  withdrawLoading: false,
  detailsLoading: false,
};

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setSelectedOffer: (state, action) => {
      state.selectedOffer = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOffersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOffersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(getOffersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getOfferByIdThunk.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(getOfferByIdThunk.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedOffer = action.payload;
      })
      .addCase(getOfferByIdThunk.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload;
      })
      .addCase(acceptOfferThunk.pending, (state) => {
        state.acceptLoading = true;
        state.error = null;
      })
      .addCase(acceptOfferThunk.fulfilled, (state) => {
        state.acceptLoading = false;
      })
      .addCase(acceptOfferThunk.rejected, (state, action) => {
        state.acceptLoading = false;
        state.error = action.payload;
      })
      .addCase(declineOfferThunk.pending, (state) => {
        state.declineLoading = true;
        state.error = null;
      })
      .addCase(declineOfferThunk.fulfilled, (state) => {
        state.declineLoading = false;
      })
      .addCase(declineOfferThunk.rejected, (state, action) => {
        state.declineLoading = false;
        state.error = action.payload;
      })
      .addCase(updateDOJThunk.pending, (state) => {
        state.dojLoading = true;
        state.error = null;
      })
      .addCase(updateDOJThunk.fulfilled, (state, action) => {
        state.dojLoading = false;
        if (state.selectedOffer) {
          state.selectedOffer = { ...state.selectedOffer, ...action.payload };
        }
      })
      .addCase(updateDOJThunk.rejected, (state, action) => {
        state.dojLoading = false;
        state.error = action.payload;
      })
      .addCase(withdrawOfferThunk.pending, (state) => {
        state.withdrawLoading = true;
        state.error = null;
      })
      .addCase(withdrawOfferThunk.fulfilled, (state) => {
        state.withdrawLoading = false;
      })
      .addCase(withdrawOfferThunk.rejected, (state, action) => {
        state.withdrawLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedOffer, clearError } = offersSlice.actions;
export default offersSlice.reducer;
